import { ReactNode } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import useViewerId from '@/hooks/useViewerId'
import { AuthUser } from '@/types/users'
import { ApproverInfo } from '@/utils/approver-entries'
import ApprovalApproverRow from './ApprovalApproverRow'

type PolicySectionProps = {
  label: string
  adornment?: ReactNode
  children: ReactNode
}

function PolicySection({ label, adornment, children }: PolicySectionProps) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
        >
          {label}
        </Typography>
        {adornment}
      </Stack>
      {children}
    </Box>
  )
}

type PolicyConditionsProps = {
  text?: string
}

export function PolicyConditions({ text }: PolicyConditionsProps) {
  if (!text) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unavailable
      </Typography>
    )
  }

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </Typography>
  )
}

type ApprovalPolicyInfoProps = {
  conditions: ReactNode
  orderLabel?: string | null
  approvers: ApproverInfo[]
  /** Approvers by pd user id — see `useUsersById`. */
  usersById: Map<string, AuthUser>
  /** Names for the positions a policy's approver list holds, by position id. */
  positionNamesById?: Map<string, string>
  positionsPending?: boolean
  usersPending?: boolean
}

/**
 * What one approval policy asks for: the condition that makes it apply and
 * who has to approve, in order. Rows show each approver's decision where the
 * document tracks the policy, and never offer approve/reject controls.
 */
export default function ApprovalPolicyInfo({
  conditions,
  orderLabel,
  approvers,
  usersById,
  positionNamesById,
  positionsPending = false,
  usersPending = false,
}: ApprovalPolicyInfoProps) {
  const viewerId = useViewerId()

  return (
    <Stack spacing={2}>
      <PolicySection label="Conditions">{conditions}</PolicySection>

      <PolicySection
        label={`Approvers (${approvers.length})`}
        adornment={
          orderLabel && (
            <Chip
              size="small"
              label={orderLabel}
              sx={{ flex: 'none', fontWeight: 600, letterSpacing: '0.04em' }}
            />
          )
        }
      >
        {/* Pulled out by the rows' own px so the status circles line up with
            the section label, the same way the approval card does it. */}
        <Stack sx={{ mt: 0.25, mx: -1 }}>
          {approvers.map((approver) => {
            const { ref } = approver
            const isPosition = ref.kind === 'position'
            const actedByUserId = approver.latestAction?.acted_by_user_id

            return (
              <ApprovalApproverRow
                key={approver.key}
                latestAction={approver.latestAction}
                positionName={
                  isPosition
                    ? positionNamesById?.get(ref.positionId) ??
                      'Unknown position'
                    : undefined
                }
                user={isPosition ? undefined : usersById.get(ref.userId)}
                isUserPending={usersPending}
                isPositionPending={positionsPending}
                isViewer={!isPosition && ref.userId === viewerId}
                approvalOrder={approver.approvalOrder}
                stepNumber={approver.stepNumber}
                isCurrentTurn={approver.isCurrentTurn}
                isFutureStep={approver.isFutureStep}
                notNeeded={approver.notNeeded}
                actedByUser={
                  actedByUserId ? usersById.get(actedByUserId) : undefined
                }
              />
            )
          })}
        </Stack>
      </PolicySection>
    </Stack>
  )
}
